import React, {PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

export type OpenerType = 'single-arrow' | 'double-arrow' | 'caret' | null;

interface Props {
  isOpen: boolean;
  isActive?: boolean;
  openerType?: OpenerType;
  onClick?: () => void;
}
export const ContentOpener = React.forwardRef<
  HTMLButtonElement,
  PropsWithChildren<Props>
>(function ContentOpener(
  {children, isOpen, isActive, onClick, openerType, ...rest},
  ref,
) {
  return (
    <ContentOpenerWrapper
      $isOpen={isOpen}
      $isActive={isActive}
      onClickCapture={() => onClick?.()}
      data-testid="opener"
      role="button"
      {...rest}
      ref={ref}
    >
      {children}
      {openerType && (
        <OpenerArrow
          openerType={openerType}
          isOpen={isOpen}
          isActive={isActive}
        />
      )}
    </ContentOpenerWrapper>
  );
});

const OpenerArrow: React.FC<{
  openerType: OpenerType;
  isOpen: boolean;
  isActive?: boolean;
}> = ({openerType, isOpen, isActive}) => {
  switch (openerType) {
    case 'single-arrow':
      return (
        <SingleArrow
          size={'XS'}
          asset={GenericIconName.Dropdown}
          $isOpen={isOpen}
          $isActive={isActive}
        />
      );
    case 'caret':
      return (
        <SingleArrow
          size={'XXS'}
          asset={GenericIconName.ArrowDown}
          $isOpen={isOpen}
          $isActive={isActive}
        />
      );
    case 'double-arrow':
    default:
      return (
        <DoubleArrow
          size={'XS'}
          asset={GenericIconName.DoubleArrow}
          $isOpen={isOpen}
          $isActive={isActive}
        />
      );
  }
};

const StyledIcon = styled(Icon)<{$isOpen: boolean; $isActive?: boolean}>`
  display: inline;
  margin-right: 0.5rem;
  opacity: ${({$isOpen, $isActive}) => ($isActive || $isOpen ? 1 : 0.4)};
`;

export const ContentOpenerWrapper = styled.button<{
  $isOpen: boolean;
  $isActive?: boolean;
}>`
  display: flex;
  align-items: center;
  cursor: pointer;
  border: solid 1px
    ${({$isActive}) => ($isActive ? COLORS.Black : COLORS.Grey3)};
  color: ${({$isOpen, $isActive}) =>
    $isActive || $isOpen ? COLORS.Black : COLORS.Grey1};
  background-color: ${COLORS.White};
  border-radius: 4px;
  position: relative;
  padding: 0;
  width: 100%;

  &:hover {
    color: ${COLORS.Black};

    ${StyledIcon} {
      opacity: 1;
    }
  }

  justify-content: space-between;
`;

const SingleArrow = styled(StyledIcon)<{$isOpen: boolean; $isActive?: boolean}>`
  margin-top: 0.2rem;

  ${({$isOpen}) =>
    $isOpen &&
    css`
      transform: scale(-1);
    `}
`;

const DoubleArrow = styled(StyledIcon)`
  margin-top: 2px;
`;
